import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Field, FieldGroup } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { parseError } from '@/lib/errors'
import { type OrgBusiness, updateBusiness } from '@/models/business'

interface FormData {
  name: string
  dotNumber: string
  mcNumber: string
}

interface EditBusinessDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
  business: OrgBusiness
  orgId: string
}

export function EditBusinessDialog({
  open,
  onOpenChange,
  onSuccess,
  business,
  orgId,
}: EditBusinessDialogProps) {
  const [loading, setLoading] = useState(false)

  const { control, handleSubmit, reset } = useForm<FormData>({
    defaultValues: { name: '', dotNumber: '', mcNumber: '' },
  })

  useEffect(() => {
    if (open) {
      reset({
        name: business.name || '',
        dotNumber: business.dotNumber || '',
        mcNumber: business.mcNumber || '',
      })
    }
  }, [open, business, reset])

  async function onSubmit(data: FormData) {
    setLoading(true)
    try {
      await updateBusiness(orgId, {
        name: data.name.trim(),
        dotNumber: data.dotNumber.trim(),
        mcNumber: data.mcNumber.trim(),
      })
      onSuccess()
      toast.success('Motor carrier updated')
      onOpenChange(false)
    } catch (err) {
      toast.error(parseError(err))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!value) reset()
        onOpenChange(value)
      }}
    >
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle>Edit Motor Carrier</DialogTitle>
          </DialogHeader>
          <FieldGroup className="mt-4">
            <Field>
              <Label htmlFor="mc-name">Name</Label>
              <Controller
                name="name"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <Input {...field} id="mc-name" aria-invalid={fieldState.error ? true : undefined} />
                )}
              />
            </Field>
            <Field>
              <Label htmlFor="mc-dot-number">DOT number</Label>
              <Controller
                name="dotNumber"
                control={control}
                render={({ field }) => <Input {...field} id="mc-dot-number" />}
              />
            </Field>
            <Field>
              <Label htmlFor="mc-mc-number">MC number</Label>
              <Controller
                name="mcNumber"
                control={control}
                render={({ field }) => <Input {...field} id="mc-mc-number" />}
              />
            </Field>
          </FieldGroup>
          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={loading ? 'Saving...' : undefined}>
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
